import React from 'react';
import './Pricing.css';
import {Box, Button, Card, CardActions, Divider, List, ListItem, ListItemDecorator, Typography} from "@mui/joy";
import {Check, KeyboardArrowRight} from "@mui/icons-material";

const pricingPlans = [
    {
        title: "Basic",
        price: "$10/year",
        buttonText: "Get Started",
        isPopular: false,
        description: "Ideal for small leagues."
    },
    {
        title: "Advanced",
        price: "$15/year",
        buttonText: "Get Started",
        isPopular: false,
        description: "For leagues needing enhanced scoring options."
    },
    {
        title: "Community",
        price: "$35/year",
        buttonText: "Contact sales",
        isPopular: false,
        description: "Perfect for mid-sized golf clubs or community leagues."
    },
    {
        title: "Enterprise",
        price: "Custom Pricing",
        buttonText: "Contact sales",
        isPopular: false,
        description: "Designed for large-scale or professional organizations."
    }
];

const pricingFeatures = [
    {
        feature: 'Max Golf Leagues',
        description: "Max golf leagues per account",
        plans: [1, 1, 5, "Unlimited"]
    },
    {
        feature: 'Automatic Net Score Calculation',
        description: "Calculates net scores based on player handicaps.",
        plans: [true, true, true, true]
    },
    {
        feature: 'Top Player Payouts',
        description: "Automatically calculated payouts for the best gross and net scores.",
        plans: [true, true, true, true]
    },
    {
        feature: 'Runner-Up Player Payouts',
        description: "Rewards for additional-best gross and net scores.",
        plans: [true, true, true, true]
    },
    {
        feature: 'Closest-to-Pin Challenge',
        description: "Tracks and rewards the closest-to-the-pin shots.",
        plans: [true, true, true, true]
    },
    {
        feature: 'Birdie Bonuses',
        description: "Automatically calculates payouts for birdies.",
        plans: [true, true, true, true]
    },
    {
        feature: 'Monthly League Insights (Coming Soon)',
        description: "Summarizes player and league stats each month.",
        plans: [true, true, true, true]
    },
    {
        feature: 'Printable League Summaries',
        description: "Automatically generated printable summaries of match scores and payouts",
        plans: [true, true, true, true]
    },
    {
        feature: 'Event Scheduling',
        description: "Simplifies scheduling tournaments or games.",
        plans: [true, true, true, true]
    },
    {
        feature: 'Buy-In Management (Coming Soon)',
        description: "Tracks buy-in contributions for players.",
        plans: [false, true, true, true]
    },
    {
        feature: 'Course Information (Coming Soon)',
        description: "Stores par information on courses",
        plans: [false, true, true, true]
    },
    {
        feature: 'Max Courses (Coming Soon)',
        description: "Max courses per account",
        plans: [false, 1, 3, "Unlimited"]
    },
    {
        feature: 'Prize Pool Management (Coming Soon)',
        description: "Tracks available prize pool funds.",
        plans: [false, true, true, true]
    },
    {
        feature: 'Prize Pool Analytics (Coming Soon)',
        description: "Tracks trends in payouts and buy-ins.",
        plans: [false, true, true, true]
    },
    {
        feature: 'Dynamic Handicap Adjustments (Coming Soon)',
        description: "Recalculates handicaps based on league rules.",
        plans: [false, true, true, true]
    },
    {
        feature: 'Live Scoring (Coming Soon)',
        description: "Realtime score reporting for players.",
        plans: [false, false, true, true]
    },
    {
        feature: 'Live Leaderboards (Coming Soon)',
        description: "Real-time leaderboard tracking during tournaments.",
        plans: [false, false, true, true]
    },
    {
        feature: 'Remove Branding (Coming Soon)',
        description: "Removes platform branding for a professional look.",
        plans: [false, false, false, true]
    },
    {
        feature: 'Tailored Reports (Coming Soon)',
        description: "Create custom scoring and payout reports to fit your needs.",
        plans: [false, false, false, true]
    }
];

const PricingComparisonTable = () => (
    <div className="pricing-comparison-container">
        <table className="pricing-comparison-table">
            <thead>
            <tr>
                <th width={"20%"}>Features</th>
                {pricingPlans.map((plan, index) => (
                    <th key={index} width={"20%"}>
                        <div className={`plan-header ${plan.isPopular ? 'popular' : ''}`}>
                            {plan.isPopular && <span className="badge">Most Popular</span>}
                            <h3>{plan.title}</h3>
                            <p className="description">{plan.description}</p>
                            <p className="price">{plan.price}</p> {/* Add this line */}
                        </div>
                    </th>
                ))}
            </tr>
            </thead>
            <tbody>
            {pricingFeatures.map((feature, index) => (
                <tr key={index}>
                    <td>
                        {feature.feature}
                        <br/>
                        <p className="feature-description">{feature.description}</p>
                    </td>
                    {feature.plans.map((plan, i) => (
                        <td key={i}>
                            {typeof plan === 'boolean' ? (plan ? <Check/> : '') : plan}
                        </td>
                    ))}
                </tr>
            ))}
            </tbody>
        </table>
    </div>
);

const App = () => {
    const handleContactSales = () => {
        window.location.href = "https://golf-league-tools.atlassian.net/servicedesk/customer/portal/5";
    }
    return (
        <div className="container">

            <div className="container">
                <Box
                    sx={{
                        width: '100%',
                        display: 'grid',
                        gridTemplateColumns: '4fr 4fr 4fr 4fr',
                        gap: 2,
                    }}
                >
                    <Card size="lg" variant="outlined">
                        {/*<Chip size="sm" variant="outlined" color="neutral">*/}
                        {/*    BASIC*/}
                        {/*</Chip>*/}
                        <Typography level="h2">Basic</Typography>
                        <Typography level="body-sm">1 League</Typography>
                        <Divider inset="none"/>
                        <List size="sm">
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Automatic Net Score Calculation
                            </ListItem>
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Top Player Payouts
                            </ListItem>
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Runner-Up Player Payouts
                            </ListItem>
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Closest-to-Pin Challenge
                            </ListItem>
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Birdie Bonuses
                            </ListItem>
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Monthly League Insights (Coming Soon)
                            </ListItem>
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Printable League Summaries
                            </ListItem>
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Event Scheduling
                            </ListItem>
                        </List>
                        <Divider inset="none"/>
                        <CardActions>
                            <Typography level="title-lg" sx={{mr: 'auto'}}>
                                $10{' '}
                                <Typography textColor="text.tertiary" sx={{fontSize: 'sm'}}>
                                    / year
                                </Typography>
                            </Typography>
                            <Button
                                variant="soft"
                                color="neutral"
                                endDecorator={<KeyboardArrowRight/>}
                            >
                                Start now
                            </Button>
                        </CardActions>
                    </Card>
                    <Card
                        size="lg"
                        variant="solid"
                        color="neutral"
                        invertedColors
                        sx={{bgcolor: 'neutral.900'}}
                    >
                        {/*<Chip size="sm" variant="outlined">*/}
                        {/*    MOST POPULAR*/}
                        {/*</Chip>*/}
                        <Typography level="h2">Advanced</Typography>
                        <Typography level="body-sm">1 League</Typography>
                        <Divider inset="none"/>
                        <Typography level="body-sm">Everything in Basic, Plus:</Typography>
                        <List
                            size="sm"
                        >
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                1 Course (Coming Soon)
                            </ListItem>
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Buy-In Management (Coming Soon)
                            </ListItem>
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Course Information (Coming Soon)
                            </ListItem>
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Prize Pool Management (Coming Soon)
                            </ListItem>
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Prize Pool Analytics (Coming Soon)
                            </ListItem>
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Dynamic Handicap Adjustments (Coming Soon)
                            </ListItem>
                        </List>
                        <Divider inset="none"/>
                        <CardActions>
                            <Typography level="title-lg" sx={{mr: 'auto'}}>
                                $15{' '}
                                <Typography textColor="text.tertiary" sx={{fontSize: 'sm'}}>
                                    / year
                                </Typography>
                            </Typography>
                            <Button endDecorator={<KeyboardArrowRight/>}>Start now</Button>
                        </CardActions>
                    </Card>
                    <Card
                        size="lg"
                        variant="solid"
                        color="neutral"
                        invertedColors
                        sx={{bgcolor: 'neutral.900'}}
                    >
                        {/*<Chip size="sm" variant="outlined">*/}
                        {/*    MOST POPULAR*/}
                        {/*</Chip>*/}
                        <Typography level="h2">Community</Typography>
                        <Typography level="body-sm">5 Leagues</Typography>
                        <Divider inset="none"/>
                        <Typography level="body-sm">Everything in Advanced, Plus:</Typography>
                        <List
                            size="sm"
                        >
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                3 Courses (Coming Soon)
                            </ListItem>
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Live Scoring (Coming Soon)
                            </ListItem>
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Live Leaderboards (Coming Soon)
                            </ListItem>
                        </List>
                        <Divider inset="none"/>
                        <CardActions>
                            <Typography level="title-lg" sx={{mr: 'auto'}}>
                                $35{' '}
                                <Typography textColor="text.tertiary" sx={{fontSize: 'sm'}}>
                                    / year
                                </Typography>
                            </Typography>
                            <Button  onClick={() => handleContactSales()}
                                endDecorator={<KeyboardArrowRight/>}>Contact Sales</Button>
                        </CardActions>
                    </Card>
                    <Card
                        size="lg"
                        variant="solid"
                        color="neutral"
                        invertedColors
                        sx={{bgcolor: 'neutral.900'}}
                    >
                        {/*<Chip size="sm" variant="outlined">*/}
                        {/*    MOST POPULAR*/}
                        {/*</Chip>*/}
                        <Typography level="h2">Enterprise</Typography>
                        <Typography level="body-sm">Unlimited Leagues</Typography>
                        <Divider inset="none"/>
                        <Typography level="body-sm">Everything in Community, Plus:</Typography>
                        <List
                            size="sm"
                        >
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Unlimited Courses (Coming Soon)
                            </ListItem>
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Remove Branding (Coming Soon)
                            </ListItem>
                            <ListItem>
                                <ListItemDecorator>
                                    <Check/>
                                </ListItemDecorator>
                                Tailored Reports (Coming Soon)
                            </ListItem>
                        </List>
                        <Divider inset="none"/>
                        <CardActions>
                            <Typography level="title-lg" sx={{mr: 'auto'}}>
                                <Typography textColor="text.tertiary" sx={{fontSize: 'sm'}}>
                                    Custom Pricing
                                </Typography>
                            </Typography>
                            <Button onClick={() => handleContactSales()}
                                    endDecorator={<KeyboardArrowRight/>}>Contact Sales</Button>
                        </CardActions>
                    </Card>
                </Box>

                <h1>Compare Our Plans</h1>
                <PricingComparisonTable/>
            </div>
        </div>
    );
}

export default App;